import React, { useContext, useRef, useState } from 'react'

import { Form, FormInstance, Input, InputNumber } from 'antd'

import Select from 'live/components/forms/ui/select'
import { fetchCodafEntity } from '../services/api'
import moment from 'moment'
import { formatDate } from './components/CustomDateTimePicker'

const EditableContext = React.createContext<FormInstance<any> | null>(null)

const getRestrictedPerformanceFields = (dataIndex: string) => ({
    main: dataIndex,
    acronym: `${dataIndex}Acronym`,
    value: `${dataIndex}Value`,
    uom: `${dataIndex}Uom`
})

// =============================================

interface EditableRowProps {
    index: number
}

export const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
    const [form] = Form.useForm()

    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    )
}

export type Row =
    | WellDto
    | InterventionsDto
    | SectionDto
    | RigTableDto
    | RunDto
    | CementedPlugDto
    | TubularTypeDto
    | TubularRestrictedPipeMovementsDto
    | TubularRestrictedConnectionDto
    | TubularRestrictedRopDto

export type EditableCellType = 'text' | 'number' | 'date' | 'select'

// =============================================

interface EditableCellProps {
    title: React.ReactNode
    editable: boolean
    children: React.ReactNode
    cellType: EditableCellType
    dataIndex: keyof Row
    record: Row
    options: SelectOptions[]
    allOptions: { wellType: SelectOptions[]; referenceSystem: SelectOptions[]; wellCategory: SelectOptions[] }
    onEditSave: (record: Row) => void
    localErrors: LocalErrorsTypes[]
}

export const EditableCell: React.FC<EditableCellProps> = ({
    title,
    editable,
    children,
    cellType,
    dataIndex,
    record,
    options,
    allOptions,
    onEditSave,
    localErrors,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false)
    const form = useContext(EditableContext)
    const inputRef = useRef<any>(null)
    const isRestrictedPerformance =
        dataIndex === ('restrictedPipeMovementSpeed' as string) ||
        dataIndex === ('restrictedTimeConnection' as string) ||
        dataIndex === ('restrictedRopSpeed' as string)

    const isAttributeUnit =
        dataIndex === ('waterDepth' as string) ||
        dataIndex === ('startMD' as string) ||
        dataIndex === ('finalMD' as string) ||
        dataIndex === ('oD' as string) ||
        dataIndex === ('prevCasingOD' as string) ||
        dataIndex === ('prevCasingFinalMD' as string)
    const isTacticColumn = dataIndex === ('tactic' as string)

    const hasCellError =
        localErrors?.length > 0 &&
        localErrors.some(error => error.uuid === record?.frontUuid && error.columnName === dataIndex)
    const toggleEdit = () => {
        setEditing(!editing)
        form.setFieldsValue({ [dataIndex]: record[dataIndex] })
    }

    const getCodafEntity = async (
        option: SelectOptions,
        entityName: string
    ): Promise<SelectOptions | WellType | RigType> => {
        const allCodafEntities = await fetchCodafEntity(entityName)
        const newEntity: WellType | RigType = allCodafEntities?.find(
            entity => entity?.assetId && option?.value && Number(entity?.assetId) === Number(option?.value)
        )

        if (newEntity) {
            return newEntity
        }

        return option
    }

    const mountWellRowByCodafInfo = (codafWellOptions: WellType): WellType => {
        return {
            wellId: codafWellOptions.assetId,
            wellName: codafWellOptions.name,
            wellIdInCodaf: codafWellOptions.id,
            fieldName: codafWellOptions.fieldName,
            waterDepth: (codafWellOptions.waterDepth as AttributeUnit).value,
            waterDepthAsAttributeUnit: codafWellOptions.waterDepth,
            referenceSystem: getCellValue(allOptions.referenceSystem, codafWellOptions.referenceSystem)?.label,
            wellType: getCellValue(allOptions.wellType, codafWellOptions.wellType)?.label,
            latitude: codafWellOptions.latitude,
            longitude: codafWellOptions.longitude,
            wellCategory: getCellValue(allOptions.wellCategory, codafWellOptions.wellCategory)?.label,
            name: codafWellOptions.name,
            assetId: codafWellOptions.assetId,
            uuid: codafWellOptions.uuid
        } as WellType
    }

    const save = async (option?: any) => {
        try {
            let values = await form.validateFields()
            // Dealing with a specific case: Well and Rig select
            if (dataIndex === ('wellName' as string)) {
                const codafWell = await getCodafEntity(option, 'well')
                if (!option) {
                    values = {
                        wellId: null,
                        wellName: null,
                        name: null,
                        assetId: null,
                        uuid: null
                    }
                } else if ((codafWell as SelectOptions)?.value) {
                    const options = codafWell as SelectOptions
                    values = {
                        wellId: options.value,
                        wellName: options.label,
                        name: options.label,
                        assetId: options.value,
                        uuid: undefined
                    }
                } else {
                    values = mountWellRowByCodafInfo(codafWell as WellType)
                }
            } else if (dataIndex === ('rigName' as string)) {
                const codafRig = await getCodafEntity(option, 'rig')
                values = {
                    rigId: (codafRig as RigType)?.assetId ?? option?.value,
                    rigIdInCodaf: (codafRig as RigType)?.id,
                    rigName: option?.label,
                    assetName: option?.label
                }
            } else if (isRestrictedPerformance) {
                const fields = getRestrictedPerformanceFields(dataIndex as string)
                const acronym = option?.label
                    .split(' - ')
                    .slice(1)
                    .join(' - ')
                values = {
                    [fields.main]: {
                        id: 0,
                        name: option?.value?.uom?.name,
                        value: option?.value?.value
                    },
                    [fields.acronym]: `- ${acronym}`,
                    [fields.value]: option?.value?.value,
                    [fields.uom]: option?.value?.uom?.name
                }
            } else if (isAttributeUnit) {
                values = {
                    [dataIndex]: values[dataIndex] ? Number(values[dataIndex]) : null,
                    [dataIndex + 'Value']: values[dataIndex] ? Number(values[dataIndex]) : null
                }
            } else if (isTacticColumn) {
                values = { tatic: option?.label, tactic: option?.label }
            } else {
                values[dataIndex] = option?.label ?? values[dataIndex]
            }

            toggleEdit()
            onEditSave({ ...record, ...values })
        } catch (errInfo) {
            console.log('Save failed:', errInfo)
        }
    }

    const getCellValue = (currentOptions: SelectOptions[], value?: string): SelectOptions => {
        let cellValue: SelectOptions = null
        currentOptions = currentOptions?.map(option => ({ ...option, label: option.label?.trim() }))
        if (currentOptions?.length > 0) {
            for (const option of currentOptions) {
                if (isRestrictedPerformance) {
                    const fields = getRestrictedPerformanceFields(dataIndex as string)
                    const restrictedLabel = `${((record[fields.main] as unknown) as Uom)?.value} ${
                        ((record[fields.main] as unknown) as Uom)?.name
                    } ${String(record[fields.acronym])?.trim()}`
                    if (option.label === restrictedLabel) {
                        cellValue = option
                        break
                    }
                }

                if (
                    option.label === record[dataIndex] ||
                    option.value === record[dataIndex] ||
                    option.value === value
                ) {
                    cellValue = option
                    break
                }
            }
        }

        return cellValue
    }

    const getEditableComponent = (): JSX.Element => {
        if (cellType == 'number') {
            return <InputNumber size="small" ref={inputRef} onPressEnter={save} onBlur={save} />
        }

        return (
            <Input
                className={`${cellType || ''}`}
                size="small"
                ref={inputRef}
                onPressEnter={save}
                onBlur={save}
                autoFocus
            />
        )
    }

    let childNode = children

    if (editable) {
        childNode =
            editing && cellType !== 'select' ? (
                <Form.Item name={dataIndex}>{getEditableComponent()}</Form.Item>
            ) : (
                <div className="editable-cell-value-wrap" onClick={toggleEdit}>
                    {cellType === 'select' ? (
                        <Select
                            options={options ?? []}
                            ref={inputRef}
                            value={getCellValue(options) ?? ''}
                            onChange={(id, objValue) => save(objValue)}
                        />
                    ) : (
                        children
                    )}
                </div>
            )
    }

    let titleTooltip = (dataIndex ? record[dataIndex] : '') as string

    if (String(cellType)?.includes('calendar')) {
        try {
            titleTooltip = moment(new Date(titleTooltip), formatDate).isValid()
                ? `${moment(new Date(titleTooltip), formatDate).format(`${formatDate} Z`)}`
                : titleTooltip
        } catch {
            titleTooltip = String(titleTooltip)
        }
    }

    return (
        <td {...restProps}>
            <div
                style={{
                    border: `1px solid ${hasCellError ? 'var(--red-500)' : 'transparent'}`
                }}
                className={`td-content ${cellType || ''}`}
                title={titleTooltip}
            >
                {childNode}
            </div>
        </td>
    )
}
